import { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Search, ChevronUp, ChevronDown, X, Clock, Percent, Pencil, RefreshCw, Check, Palette, Plus } from "lucide-react";

const QUICK_STYLES = [
  { label: "White", dot: "#FFFFFF", patch: { fillColor: "#FFFFFF" } },
  { label: "Red", dot: "#FF3B30", patch: { fillColor: "#FF3B30" } },
  { label: "Yellow", dot: "#FFD400", patch: { fillColor: "#FFD400" } },
  { label: "Glow", dot: "#00F0FF", patch: { glowEnabled: true, glowColor: "#00F0FF" } },
  { label: "3D", patch: { threeDEnabled: true, threeDColor: "#000000", threeDDepth: 7 } },
  { label: "Pop", patch: { animation: "pop" } },
  { label: "Bounce", patch: { animation: "bouncy" } },
];
import { fmtTime } from "@/lib/format";

export default function TranscriptPanel({ segments, activeWordId, onSeek, onChange, selectedWordId, setSelectedWordId, onStyleWord, styledWordIds = {}, onQuickStyle, projectId, showDelete = false }) {
  const [query, setQuery] = useState("");
  const [matchIdx, setMatchIdx] = useState(0);
  const [editing, setEditing] = useState(null); // {sid, widx, value}
  const [inserting, setInserting] = useState(false);
  const [insertVal, setInsertVal] = useState("");
  useEffect(() => { setInserting(false); setInsertVal(""); }, [selectedWordId]);
  const scrollRef = useRef(null);
  const wordRefs = useRef({});

  const wordCount = useMemo(() => segments.reduce((a, s) => a + (s.words?.length || 0), 0), [segments]);

  const matches = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.trim().toLowerCase();
    const out = [];
    segments.forEach((s) => (s.words || []).forEach((w, i) => {
      if (w.word.toLowerCase().includes(q)) out.push({ sid: s.id, widx: i, wid: w.word_id, start: w.start });
    }));
    return out;
  }, [query, segments]);

  useEffect(() => { setMatchIdx(0); }, [query]);
  useEffect(() => {
    if (matches.length && matches[matchIdx]) {
      const el = wordRefs.current[matches[matchIdx].wid];
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [matchIdx, matches]);

  // auto-scroll to active word when it changes
  useEffect(() => {
    if (!activeWordId) return;
    const el = wordRefs.current[activeWordId];
    if (el && scrollRef.current) {
      const c = scrollRef.current, r = el.getBoundingClientRect(), cr = c.getBoundingClientRect();
      if (r.top < cr.top + 60 || r.bottom > cr.bottom - 60) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeWordId]);

  const matchSet = useMemo(() => new Set(matches.map((m) => m.wid)), [matches]);

  const deleteWord = (sid, widx) => {
    const seg = segments.find((s) => s.id === sid);
    const removedId = seg?.words?.[widx]?.word_id;
    const next = segments
      .map((s) => {
        if (s.id !== sid) return s;
        const words = s.words.filter((_, i) => i !== widx);
        return { ...s, words, text: words.map((w) => w.word).join(" ") };
      })
      .filter((s) => (s.words || []).length > 0);
    onChange(next);
    if (removedId && removedId === selectedWordId) setSelectedWordId(null);
  };

  const commitWordText = (sid, widx, value) => {
    const next = segments.map((s) => {
      if (s.id !== sid) return s;
      const words = s.words.map((w, i) => (i === widx ? { ...w, word: value } : w));
      return { ...s, words, text: words.map((w) => w.word).join(" ") };
    });
    onChange(next);
  };

  const updateWordField = (sid, widx, field, value) => {
    const next = segments.map((s) => {
      if (s.id !== sid) return s;
      const words = s.words.map((w, i) => (i === widx ? { ...w, [field]: value } : w));
      return { ...s, words };
    });
    onChange(next);
  };

  const recalcWord = async (sid, widx) => {
    const seg = segments.find((s) => s.id === sid);
    const w = seg.words[widx];
    const parts = w.word.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) return;
    let out = null, aligned = false;
    if (projectId) {
      try {
        const r = await api.realign(projectId, { segment_id: sid, start: w.start, end: w.end, text: w.word });
        out = r.words; aligned = r.aligned;
      } catch { /* fall through to proportional */ }
    }
    if (!out) {
      const step = (w.end - w.start) / parts.length;
      out = parts.map((p, i) => ({ word: p, start: +(w.start + i * step).toFixed(3), end: +(w.start + (i + 1) * step).toFixed(3) }));
    }
    const newWords = out.map((p, i) => ({
      word: p.word, start: p.start, end: p.end,
      confidence: w.confidence, segment_id: sid, word_id: `${sid}-r${widx}-${i}`,
    }));
    const next = segments.map((s) => {
      if (s.id !== sid) return s;
      const words = [...s.words.slice(0, widx), ...newWords, ...s.words.slice(widx + 1)];
      return { ...s, words, text: words.map((x) => x.word).join(" ") };
    });
    onChange(next);
    setSelectedWordId(null);
    toast[aligned ? "success" : "warning"](aligned
      ? "New words aligned to the audio"
      : "Couldn't confidently align to audio — used even spacing. Adjust the timings if needed.");
  };

  // Insert a brand-new word AFTER the given word. Existing word IDs/timings are
  // preserved; only the new word is aligned to the audio (via /realign on the
  // gap region). If alignment isn't confident, it drops into the gap and the
  // user can adjust Start/End manually.
  const insertWordAfter = async (sid, widx, text) => {
    const word = (text || "").trim();
    if (!word) return;
    const seg = segments.find((s) => s.id === sid);
    if (!seg) return;
    const cur = seg.words[widx];
    const nxt = seg.words[widx + 1];
    const gapStart = cur.end;
    const gapEnd = nxt ? nxt.start : (seg.end != null ? Math.max(seg.end, gapStart + 0.3) : gapStart + 0.6);
    let start = gapStart;
    let end = gapEnd > gapStart + 0.05 ? gapEnd : gapStart + 0.4;
    let aligned = false;
    if (projectId) {
      try {
        const r = await api.realign(projectId, {
          segment_id: sid, start: gapStart,
          end: gapEnd > gapStart + 0.05 ? gapEnd : gapStart + 0.6, text: word,
        });
        if (r.aligned && r.words && r.words.length === 1) {
          start = r.words[0].start; end = r.words[0].end; aligned = true;
        }
      } catch { /* fall back to gap placement */ }
    }
    const wid = `${sid}-ins-${Date.now()}`;
    const newWord = {
      word, start: +Number(start).toFixed(3), end: +Number(end).toFixed(3),
      confidence: aligned ? 0.9 : 0.3, segment_id: sid, word_id: wid,
    };
    const next = segments.map((s) => {
      if (s.id !== sid) return s;
      const words = [...s.words.slice(0, widx + 1), newWord, ...s.words.slice(widx + 1)];
      return { ...s, words, text: words.map((w) => w.word).join(" ") };
    });
    onChange(next);
    setSelectedWordId(wid);
    setInserting(false); setInsertVal("");
    toast[aligned ? "success" : "warning"](aligned
      ? `Inserted “${word}” and aligned it to the audio`
      : `Inserted “${word}” — couldn't confidently align; adjust Start/End if needed.`);
  };


  const selected = useMemo(() => {
    if (!selectedWordId) return null;
    for (const s of segments) {
      const idx = (s.words || []).findIndex((w) => w.word_id === selectedWordId);
      if (idx >= 0) return { seg: s, widx: idx, word: s.words[idx] };
    }
    return null;
  }, [selectedWordId, segments]);

  return (
    <div className="flex h-full flex-col bg-[#0d0d0d]">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <div>
          <div className="font-display text-sm font-bold uppercase tracking-widest text-white">Transcript</div>
          <div className="text-xs text-[#A1A1AA]">{wordCount} words · {segments.length} sentences</div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2.5">
          <Search size={14} className="text-[#A1A1AA]" />
          <input data-testid="transcript-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search words…"
            className="w-full bg-transparent py-2 text-sm outline-none placeholder:text-[#666]" />
          {query && <button onClick={() => setQuery("")}><X size={13} className="text-[#A1A1AA] hover:text-white" /></button>}
        </div>
        {query && (
          <div className="flex items-center gap-1 text-xs text-[#A1A1AA]">
            <span className="tabular-nums">{matches.length ? matchIdx + 1 : 0}/{matches.length}</span>
            <button data-testid="search-prev" disabled={!matches.length} onClick={() => setMatchIdx((i) => (i - 1 + matches.length) % matches.length)} className="rounded p-1 hover:bg-white/10 disabled:opacity-30"><ChevronUp size={14} /></button>
            <button data-testid="search-next" disabled={!matches.length} onClick={() => { const n = (matchIdx + 1) % matches.length; setMatchIdx(n); onSeek(matches[n]?.start ?? 0); }} className="rounded p-1 hover:bg-white/10 disabled:opacity-30"><ChevronDown size={14} /></button>
          </div>
        )}
      </div>

      {/* Word info panel */}
      {selected && (
        <div className="border-b border-white/10 bg-[#121212] p-4" data-testid="word-info-panel">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#8B5CF6]">Word</span>
            <button onClick={() => setSelectedWordId(null)}><X size={14} className="text-[#A1A1AA] hover:text-white" /></button>
          </div>
          <input data-testid="word-edit-text" value={selected.word.word}
            onChange={(e) => updateWordField(selected.seg.id, selected.widx, "word", e.target.value)}
            className="mb-3 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 font-display text-lg font-bold outline-none focus:border-[#00F0FF]/50" />
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-wider text-[#A1A1AA]">Start</div>
              <input data-testid="word-edit-start" type="number" step="0.01" value={selected.word.start}
                onChange={(e) => updateWordField(selected.seg.id, selected.widx, "start", parseFloat(e.target.value) || 0)}
                className="w-full rounded-md border border-white/10 bg-white/[0.03] px-1 py-1.5 text-center font-plex text-xs tabular-nums outline-none" />
            </div>
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-wider text-[#A1A1AA]">End</div>
              <input data-testid="word-edit-end" type="number" step="0.01" value={selected.word.end}
                onChange={(e) => updateWordField(selected.seg.id, selected.widx, "end", parseFloat(e.target.value) || 0)}
                className="w-full rounded-md border border-white/10 bg-white/[0.03] px-1 py-1.5 text-center font-plex text-xs tabular-nums outline-none" />
            </div>
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-wider text-[#A1A1AA]">Conf.</div>
              <div className="rounded-md border border-white/10 bg-white/[0.02] px-1 py-1.5 font-plex text-xs tabular-nums text-[#00F0FF]">{Math.round((selected.word.confidence || 0) * 100)}%</div>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-[#A1A1AA]">
            <span className="flex items-center gap-1"><Clock size={11} /> {(selected.word.end - selected.word.start).toFixed(2)}s</span>
            {selected.word.word.trim().includes(" ") && (
              <button data-testid="recalc-timing-btn" onClick={() => recalcWord(selected.seg.id, selected.widx)} className="flex items-center gap-1 rounded-md bg-[#8B5CF6]/20 px-2 py-1 font-semibold text-[#a78bfa] hover:bg-[#8B5CF6]/30">
                <RefreshCw size={11} /> Recalculate Timing
              </button>
            )}
          </div>
          {inserting ? (
            <div className="mt-3 rounded-lg border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-2" data-testid="insert-word-box">
              <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-[#00F0FF]">Insert a new word after “{selected.word.word}”</div>
              <div className="flex items-center gap-1.5">
                <input autoFocus data-testid="insert-word-input" value={insertVal}
                  onChange={(e) => setInsertVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") insertWordAfter(selected.seg.id, selected.widx, insertVal); if (e.key === "Escape") { setInserting(false); setInsertVal(""); } }}
                  placeholder="Type the word…"
                  className="min-w-0 flex-1 rounded-md border border-white/10 bg-black/40 px-2.5 py-1.5 text-sm outline-none focus:border-[#00F0FF]/60" />
                <button data-testid="insert-word-confirm" onClick={() => insertWordAfter(selected.seg.id, selected.widx, insertVal)} disabled={!insertVal.trim()}
                  className="rounded-md bg-[#00F0FF] px-3 py-1.5 text-xs font-bold text-black hover:bg-[#00F0FF]/90 disabled:opacity-40">Add</button>
                <button data-testid="insert-word-cancel" onClick={() => { setInserting(false); setInsertVal(""); }}
                  className="rounded-md border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white">Cancel</button>
              </div>
              <div className="mt-1 text-[10px] text-[#666]">Existing words keep their IDs & timing. Only the new word is aligned to the audio.</div>
            </div>
          ) : (
            <button data-testid="insert-word-btn" onClick={() => setInserting(true)}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/[0.03] py-2 text-sm font-semibold text-white transition-colors hover:border-[#00F0FF]/50">
              <Plus size={15} /> Insert new word after
            </button>
          )}
          <button data-testid="style-word-btn" onClick={() => onStyleWord?.(selected.word.word_id)}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#6d28d9] py-2.5 text-sm font-bold text-white transition-transform hover:scale-[1.01]">
            <Palette size={15} /> Style this word {styledWordIds[selected.word.word_id] ? "· custom" : ""}
          </button>
          {onQuickStyle && (
            <div className="mt-2" data-testid="quick-style-bar">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-[#666]">Quick styles — applies to this word only</div>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_STYLES.map((q) => (
                  <button key={q.label} data-testid={`quick-style-${q.label.toLowerCase()}`} onClick={() => onQuickStyle(selected.word.word_id, q.patch)}
                    className="flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.03] px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:border-[#00F0FF]/50">
                    {q.dot && <span className="h-2.5 w-2.5 rounded-full" style={{ background: q.dot }} />} {q.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Transcript body */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4" data-testid="transcript-body">
        {segments.map((seg) => {
          const active = activeWordId && seg.words?.some((w) => w.word_id === activeWordId);
          return (
            <div key={seg.id} className="mb-5">
              <button data-testid={`sentence-${seg.id}`} onClick={() => { onSeek(seg.start); }}
                className={`mb-1.5 inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 font-plex text-[11px] tabular-nums transition-colors ${active ? "text-[#00F0FF]" : "text-[#666] hover:text-[#A1A1AA]"}`}>
                {fmtTime(seg.start)} <span className="text-white/20">──</span> {fmtTime(seg.end)}
              </button>
              <p className="font-plex text-[17px] leading-[1.9]">
                {(seg.words || []).map((w, i) => {
                  const isActive = w.word_id === activeWordId;
                  const isSel = w.word_id === selectedWordId;
                  const isMatch = matchSet.has(w.word_id);
                  const isEditing = editing && editing.sid === seg.id && editing.widx === i;
                  if (isEditing) {
                    return (
                      <input key={w.word_id} autoFocus data-testid={`word-edit-inline-${w.word_id}`}
                        defaultValue={w.word}
                        onBlur={(e) => { commitWordText(seg.id, i, e.target.value); setEditing(null); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { commitWordText(seg.id, i, e.target.value); setEditing(null); } if (e.key === "Escape") setEditing(null); }}
                        className="mx-0.5 inline w-auto rounded border border-[#00F0FF] bg-black px-1 font-plex text-[17px] outline-none"
                        style={{ width: `${Math.max(w.word.length, 3) + 1}ch` }} />
                    );
                  }
                  return (
                    <span key={w.word_id} ref={(el) => (wordRefs.current[w.word_id] = el)}
                      data-testid={`word-${w.word_id}`}
                      className={`group/word relative mr-1 inline-block ${showDelete ? "align-baseline" : ""}`}>
                      <span
                        onClick={() => { onSeek(w.start); setSelectedWordId(w.word_id); }}
                        onDoubleClick={() => setEditing({ sid: seg.id, widx: i })}
                        className={`inline-block cursor-pointer rounded px-1 transition-colors ${
                          isActive ? "word-active" : isSel ? "bg-[#8B5CF6]/40 text-white" : isMatch ? "bg-[#FFD400]/30 text-white" : "text-white/85 hover:bg-white/10"
                        } ${styledWordIds[w.word_id] ? "underline decoration-[#00F0FF] decoration-2 underline-offset-4" : ""} ${w.aligned === false ? "border-b-2 border-dashed border-[#FFD400]/70" : ""}`}
                        title={w.aligned === false ? "Timing needs adjustment — click to review Start/End" : undefined}>
                        {w.word}
                      </span>
                      {showDelete && (
                        <button data-testid={`word-delete-${w.word_id}`}
                          onClick={(e) => { e.stopPropagation(); deleteWord(seg.id, i); }}
                          title="Delete word"
                          className="absolute -right-1 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-[#FF3B30] text-white opacity-0 shadow transition-opacity hover:bg-[#ff5c52] focus:opacity-100 group-hover/word:opacity-100">
                          <X size={9} strokeWidth={3} />
                        </button>
                      )}
                    </span>
                  );
                })}
              </p>
            </div>
          );
        })}
        <div className="h-24" />
      </div>
      <div className="border-t border-white/10 px-4 py-2 text-center text-[11px] text-[#666]">Click a word to seek · double-click to edit</div>
    </div>
  );
}
